/**
 * /stats/leaderboard — "MOST REJECTED THIS WEEK" (S-32 normative surface).
 *
 * Strictly opt-in members only (the server never returns a non-opted-in
 * user); top 10 by plain REJECTs received this calendar week (Mon 00:00 UTC
 * anchor, recomputed hourly server-side); own row highlighted when ranked;
 * empty state ©stats.leaderboard_empty.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { copy } from "@humble/shared";
import { Card, Mascot, rise, Skeleton, stagger, VerifiedBadge } from "../../design/index.js";
import { api } from "../../lib/api.js";
import { sessionStore, useStore } from "../../lib/store.js";
import "./stats.css";

interface BoardRow {
  rank: number;
  user: {
    id: string;
    display_name: string | null;
    avatar_media_id: string | null;
    /** Server-signed viewer-bound byte URL (plain <img> renderable). */
    avatar_url: string | null;
    verified: boolean;
  };
  rejects: number;
}

async function fetchBoard(): Promise<BoardRow[]> {
  return (await api.get("/stats/leaderboard")) as unknown as BoardRow[];
}

const MEDALS = ["🥇", "🥈", "🥉"];

export function LeaderboardScreen() {
  const [rows, setRows] = useState<BoardRow[] | null>(null);
  const myId = useStore(sessionStore, (s) => s.user?.id ?? null);

  useEffect(() => {
    fetchBoard()
      .then(setRows)
      .catch(() => setRows([]));
  }, []);

  return (
    <div className="stats-screen">
      <p className="h-caps stats-kicker">Hall of infamy</p>
      <h1 className="type-display-2 leaderboard-title">{copy.stats.leaderboard_title}</h1>
      <p className="text-dim" style={{ marginTop: 8 }}>
        Weekly count of plain rejections received. Opt-in only — everyone here chose the spotlight.
      </p>

      {rows === null ? (
        <div aria-hidden="true" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={72} radius={20} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card padding="lg" style={{ display: "flex", justifyContent: "center" }}>
          <Mascot mood="ko" quip={copy.stats.leaderboard_empty} />
        </Card>
      ) : (
        <motion.ol className="leaderboard-list" variants={stagger} initial="initial" animate="enter">
          {rows.map((r) => {
            const mine = myId !== null && r.user.id === myId;
            const avatar = r.user.avatar_url;
            return (
              <motion.li key={r.user.id} variants={rise}>
                <Card
                  padding="md"
                  className={`leaderboard-row${mine ? " leaderboard-row--me" : ""}${r.rank <= 3 ? " leaderboard-row--podium" : ""}`}
                >
                  <span className="leaderboard-row__rank">
                    {MEDALS[r.rank - 1] ?? `#${r.rank}`}
                  </span>
                  {avatar ? (
                    <img className="leaderboard-row__avatar" src={avatar} alt="" loading="lazy" />
                  ) : (
                    <span className="leaderboard-row__avatar leaderboard-row__avatar--empty" aria-hidden>
                      ✖
                    </span>
                  )}
                  <span className="leaderboard-row__name">
                    {r.user.display_name ?? "Anonymous legend"}
                    {r.user.verified && <VerifiedBadge />}
                    {mine && <span className="leaderboard-row__you"> (you)</span>}
                  </span>
                  <span className="leaderboard-row__count">
                    {r.rejects}
                    <span className="leaderboard-row__count-label"> rejects</span>
                  </span>
                </Card>
              </motion.li>
            );
          })}
        </motion.ol>
      )}

      <p className="text-faint leaderboard-footer">
        Board resets Monday 00:00 UTC. Super Rejects never count here — anonymity is anonymity.{" "}
        <Link to="/stats">Your numbers →</Link>
      </p>
    </div>
  );
}
